import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingDown, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanningDashboard = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [zeroStockItems, setZeroStockItems] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/items`);
        const items = response.data;

        // Filter for real data alerts
        // Zero Stock: Items with <= 0 on hand
        const zero = items
          .filter(i => (i.total_quantity_on_hand || 0) <= 0)
          .sort((a, b) => a.itemid.localeCompare(b.itemid))
          .slice(0, 10); // Show top 10

        // Low Stock: Items with > 0 but < 100 on hand (Placeholder logic for "Low")
        const low = items
          .filter(i => (i.total_quantity_on_hand || 0) > 0 && (i.total_quantity_on_hand || 0) < 100)
          .sort((a, b) => (a.total_quantity_on_hand || 0) - (b.total_quantity_on_hand || 0))
          .slice(0, 10);

        setLowStockItems(low);
        setZeroStockItems(zero);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning Dashboard</h1>
            <p className="text-gray-600">Inventory alerts based on current stock levels</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Zero Stock Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Stockouts (0 On Hand)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {zeroStockItems.length === 0 ? (
                <p className="text-sm text-gray-500">No stockouts detected.</p>
              ) : (
                zeroStockItems.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="border-l-4 border-red-500 bg-red-50 p-3 rounded cursor-pointer hover:bg-red-100"
                    onClick={() => navigate('/items')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{item.itemid || item.name}</span>
                      <Badge className="bg-red-100 text-red-800">0 {item.unitofmeasure || 'units'}</Badge>
                    </div>
                    <div className="text-sm text-gray-700">{item.displayname}</div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Low Stock Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingDown className="w-5 h-5 text-orange-600" />
              Low Stock (&lt; 100 On Hand)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lowStockItems.length === 0 ? (
                <p className="text-sm text-gray-500">No low stock items detected.</p>
              ) : (
                lowStockItems.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="border-l-4 border-orange-500 bg-orange-50 p-3 rounded cursor-pointer hover:bg-orange-100"
                    onClick={() => navigate('/items')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-semibold text-gray-900">{item.itemid || item.name}</span>
                      <Badge className="bg-orange-100 text-orange-800">
                        {(item.total_quantity_on_hand || 0).toLocaleString()} {item.unitofmeasure || 'units'}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-700">{item.displayname}</div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="text-center text-sm text-gray-500 mt-8">
        <p>Detailed projections available in <span className="font-semibold cursor-pointer text-blue-600" onClick={() => navigate('/planning/raw-materials')}>Raw Materials</span> and <span className="font-semibold cursor-pointer text-blue-600" onClick={() => navigate('/planning/finished-goods')}>Finished Goods</span> pages.</p>
      </div>
    </div>
  );
};

export default PlanningDashboard;